import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { SeatMapApi } from '@cineamo/api/resources/seat-map/SeatMapApi';

import { CinemaRoom, RoomObject, Seat } from '@cineamo/ui/src/seat-map/typedefs';

/**
 * Query-key roots of the seat map. Exported so apps that persist their query cache can keep these
 * out of storage: a booking page must start from the live seat plan, never from a restored copy.
 */
export const SEAT_MAP_QUERY_ROOTS = ['cinema-room-seats', 'cinema-room-objects', 'cinema-room'] as const;

const [SEATS_ROOT, ROOM_OBJECTS_ROOT, ROOM_ROOT] = SEAT_MAP_QUERY_ROOTS;

// CIN-11360: React binding for the framework-agnostic SeatMapApi (which lives in @cineamo/api).
export function useSeatMap(cinemaRoomId: ShowingDto['cinemaRoomId']): {
    seats: Seat[];
    /** Absent until the room query has resolved, and for good if no room id was ever passed. */
    room: CinemaRoom | undefined;
    roomObjects: RoomObject[];
    isLoading: boolean;
    /**
     * Any of the three reads failed (after the query's own retries) and has nothing to show: a failed background
     * refresh keeps the data it has and is no error here. `refetch` tries the failed ones again.
     */
    isError: boolean;
    /** A retry of a failed read is running. */
    isRetrying: boolean;
    refetch: () => void;
} {
    const seatMapApi = useRef(new SeatMapApi());

    const {
        data: seats = [],
        isLoading: isSeatsLoading,
        isLoadingError: isSeatsError,
        isFetching: isSeatsFetching,
        refetch: refetchSeats
    } = useQuery({
        queryKey: [SEATS_ROOT, `cinemaRoomId=${cinemaRoomId}`],
        queryFn: () => seatMapApi.current.loadSeats(cinemaRoomId),
        enabled: !!cinemaRoomId
    });

    const {
        data: roomObjects = [],
        isLoading: isRoomObjectsLoading,
        isLoadingError: isRoomObjectsError,
        isFetching: isRoomObjectsFetching,
        refetch: refetchRoomObjects
    } = useQuery({
        queryKey: [ROOM_OBJECTS_ROOT, `cinemaRoomId=${cinemaRoomId}`],
        queryFn: () => seatMapApi.current.loadRoomObjects(cinemaRoomId),
        enabled: !!cinemaRoomId
    });

    // `isPending`, not `isLoading`: a query disabled through `enabled` reports isLoading false in
    // react-query v5 even though it has no data. Callers gate their seat map on `isLoading`, so with
    // isLoading they rendered it with no room at all — which is how `data.room.depth` came to throw.
    const {
        data: room,
        isPending: isRoomPending,
        isLoadingError: isRoomError,
        isFetching: isRoomFetching,
        refetch: refetchRoom
    } = useQuery({
        queryKey: [ROOM_ROOT, `cinemaRoomId=${cinemaRoomId}`],
        queryFn: async () => {
            const loadedRoom = await seatMapApi.current.loadRoom(cinemaRoomId);
            // A query cannot hold `null` as data, and a booking page with no room must show the error state.
            if (!loadedRoom) throw new Error(`Cinema room ${cinemaRoomId} could not be loaded`);
            return loadedRoom;
        },
        enabled: !!cinemaRoomId
    });

    const isLoading = isSeatsLoading || isRoomPending || isRoomObjectsLoading;

    const isError = isSeatsError || isRoomError || isRoomObjectsError;
    const isRetrying =
        (isSeatsError && isSeatsFetching) ||
        (isRoomError && isRoomFetching) ||
        (isRoomObjectsError && isRoomObjectsFetching);
    const refetch = () => {
        if (isSeatsError) void refetchSeats();
        if (isRoomError) void refetchRoom();
        if (isRoomObjectsError) void refetchRoomObjects();
    };

    return { seats, room: room ?? undefined, roomObjects, isLoading, isError, isRetrying, refetch };
}
